import api from './apiService';

/**
 * Service for ticket-related operations
 */
const ticketService = {
  /**
   * Create a new ticket
   * @param {Object} ticketData - Ticket data (subject, message, priority, etc.)
   * @returns {Promise} - Response with created ticket
   */
  createTicket: async (ticketData) => {
    try {
      const response = await api.post('/tickets', ticketData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create ticket');
    }
  },

  /**
   * Get all tickets with pagination and filters
   * @param {Object} params - Query parameters (limit, offset, status)
   * @returns {Promise} - Response with tickets
   */
  getAllTickets: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.status) queryParams.append('status', params.status);
      
      const response = await api.get(`/tickets?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get tickets');
    }
  },

  /**
   * Get a ticket by ID
   * @param {string} id - Ticket ID
   * @returns {Promise} - Response with ticket
   */
  getTicketById: async (id) => {
    try {
      const response = await api.get(`/tickets/${id}`);
      
      // Check if freescout_data is a string and parse it if needed
      if (response.data.ticket && typeof response.data.ticket.freescout_data === 'string') {
        try {
          response.data.ticket.freescout_data = JSON.parse(response.data.ticket.freescout_data);
          console.log('Parsed ticket freescout_data from string in getTicketById');
        } catch (error) {
          console.error('Error parsing freescout_data in getTicketById:', error);
          // Keep the original string if parsing fails
        }
      }
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get ticket');
    }
  },

  /**
   * Update a ticket
   * @param {string} id - Ticket ID
   * @param {Object} updateData - Data to update
   * @returns {Promise} - Response with updated ticket
   */
  updateTicket: async (id, updateData) => {
    try {
      const response = await api.put(`/tickets/${id}`, updateData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update ticket');
    }
  },

  /**
   * Delete a ticket
   * @param {string} id - Ticket ID
   * @returns {Promise} - Response with success message
   */
  deleteTicket: async (id) => {
    try {
      const response = await api.delete(`/tickets/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete ticket');
    }
  },

  /**
   * Add a reply to a ticket
   * @param {string} id - Ticket ID
   * @param {Object} replyData - Reply data (message, status)
   * @returns {Promise} - Response with updated ticket
   */
  addTicketReply: async (id, replyData) => {
    try {
      const response = await api.post(`/tickets/${id}/reply`, replyData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add reply');
    }
  },

  /**
   * Add a reply to a ticket with attachments
   * @param {string} id - Ticket ID
   * @param {Object} replyData - Reply data (message, status)
   * @param {Array} attachments - Array of File objects
   * @returns {Promise} - Response with updated ticket
   */
  addTicketReplyWithAttachments: async (id, replyData, attachments = []) => {
    try {
      const formData = new FormData();
      formData.append('message', replyData.message);
      if (replyData.status) formData.append('status', replyData.status);
      
      // Append each attachment file
      attachments.forEach(file => {
        formData.append('attachments', file);
      });
      
      const response = await api.post(`/tickets/${id}/reply-with-attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add reply with attachments');
    }
  },

  /**
   * Upload attachment to a ticket
   * @param {string} id - Ticket ID
   * @param {File} file - File to upload
   * @returns {Promise} - Response with attachment data
   */
  uploadAttachment: async (id, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/tickets/${id}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload attachment');
    }
  },

  /**
   * Get all attachments for a ticket
   * @param {string} id - Ticket ID
   * @returns {Promise} - Response with attachments
   */
  getAttachments: async (id) => {
    try {
      const response = await api.get(`/tickets/${id}/attachments`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get attachments');
    }
  },

  /**
   * Delete an attachment
   * @param {string} ticketId - Ticket ID
   * @param {string} attachmentId - Attachment ID
   * @returns {Promise} - Response with success message
   */
  deleteAttachment: async (ticketId, attachmentId) => {
    try {
      const response = await api.delete(`/tickets/${ticketId}/attachments/${attachmentId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete attachment');
    }
  },

  /**
   * Sync attachments from FreeScout
   * @param {string} id - Ticket ID
   * @returns {Promise} - Response with sync results
   */
  syncAttachmentsFromFreeScout: async (id) => {
    try {
      const response = await api.post(`/tickets/${id}/sync-attachments`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to sync attachments from FreeScout');
    }
  },

  /**
   * Get conversations for a ticket
   * @param {string} id - Ticket ID
   * @returns {Promise} - Response with conversations
   */
  getTicketConversations: async (id) => {
    try {
      const response = await api.get(`/tickets/${id}/conversations`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get ticket conversations');
    }
  },

  /**
   * Get ticket statistics
   * @returns {Promise} - Response with ticket stats
   */
  getTicketStats: async () => {
    try {
      const response = await api.get('/tickets/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get ticket statistics');
    }
  },

  /**
   * Sync all tickets from FreeScout
   * @returns {Promise} - Response with sync results
   */
  syncTicketsFromFreeScout: async () => {
    try {
      const response = await api.post('/tickets/sync-from-freescout');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to sync tickets from FreeScout');
    }
  },

  /**
   * Get all tickets from FreeScout
   * @returns {Promise} - Response with FreeScout tickets
   */
  getAllTicketsFromFreeScout: async () => {
    try {
      const response = await api.get('/tickets/from-freescout');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get tickets from FreeScout');
    }
  },

  /**
   * Sync a specific ticket with FreeScout
   * @param {string} id - Ticket ID
   * @returns {Promise} - Response with synced ticket
   */
  syncTicketWithFreeScout: async (id) => {
    try {
      console.log('Starting FreeScout sync process for ticket:', id);
      
      // First run syncTicketsFromFreeScout and await its completion
      await ticketService.syncTicketsFromFreeScout();
      console.log('Completed sync from FreeScout before retrieving individual ticket');
      
      // Wait a brief moment to ensure database consistency after the sync
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Now fetch the updated ticket data
      const response = await api.get(`/tickets/${id}`);
      
      // Check if freescout_data is a string and parse it if needed
      if (response.data.ticket && typeof response.data.ticket.freescout_data === 'string') {
        try {
          response.data.ticket.freescout_data = JSON.parse(response.data.ticket.freescout_data);
          console.log('Parsed ticket freescout_data from string');
        } catch (error) {
          console.error('Error parsing freescout_data:', error);
          // Keep the original string if parsing fails
        }
      }
      
      // Format the response to match what the component expects
      return {
        success: true,
        message: "Ticket data retrieved successfully after sync",
        ticket: response.data.ticket
      };
    } catch (error) {
      console.error('Ticket sync error:', error);
      throw new Error(error.response?.data?.message || 'Failed to sync ticket with FreeScout');
    }
  },

  /**
   * Add sensitive credentials as a private note to a ticket
   * @param {string} id - Ticket ID
   * @param {Object} credentials - Credential information to add
   * @returns {Promise} - Response with updated ticket
   */
  addCredentialsAsNote: async (id, credentials) => {
    try {
      const response = await api.post(`/tickets/${id}/credentials`, { credentials });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add credentials');
    }
  },

  /**
   * Update ticket credit costs
   * @param {string} id - Ticket ID
   * @param {Object} creditData - Credit data (support_credits, custom_credits)
   * @returns {Promise} - Response with updated ticket
   */
  updateTicketCreditCost: async (id, creditData) => {
    try {
      const response = await api.put(`/tickets/${id}/credit-cost`, creditData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update ticket credit costs');
    }
  },

  /**
   * Confirm ticket credits
   * @param {string} id - Ticket ID
   * @returns {Promise} - Response with updated ticket
   */
  confirmTicketCredits: async (id) => {
    try {
      const response = await api.post(`/tickets/${id}/confirm-credit-cost`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to confirm ticket credits');
    }
  },

  /**
   * Get all tickets in the system (admin only)
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of tickets per page
   * @param {number} params.offset - Offset for pagination
   * @param {string} [params.status] - Filter by status
   * @param {string} [params.searchTerm] - Search term for subject/description
   * @param {string} [params.sortBy] - Field to sort by
   * @param {string} [params.sortOrder] - Sort order (asc/desc)
   * @param {number} [params.user_id] - Filter by user ID
   * @returns {Promise<Object>} Response with tickets and total count
   */
  async getAllTicketsAdmin(params) {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const response = await api.get(`/tickets/admin?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get all tickets (admin) error:', error);
      throw error;
    }
  },

  /**
   * Get ticket statistics (admin only)
   * @returns {Promise<Object>} Response with ticket statistics
   */
  async getTicketStatsAdmin() {
    try {
      const response = await api.get('/tickets/admin/stats');
      return response.data;
    } catch (error) {
      console.error('Get admin ticket stats error:', error);
      throw error;
    }
  },

  /**
   * Bulk update tickets (admin only)
   * @param {number[]} ticketIds - Array of ticket IDs to update
   * @param {Object} updateData - Data to update for the tickets
   * @returns {Promise<Object>} Response with update results
   */
  async bulkUpdateTickets(ticketIds, updateData) {
    try {
      const response = await api.put('/tickets/admin/bulk-update', {
        ticketIds,
        updateData
      });
      return response.data;
    } catch (error) {
      console.error('Bulk update tickets error:', error);
      throw error;
    }
  }
};

export default ticketService;